import { Alert, Icon, Text, View } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { StyleSheet } from 'react-native';
import { ToastOptions } from 'react-native-toast-notifications';

import { ISanityImage } from '@rbi-ctg/menu';
import LoyaltyPointsMedal from 'components/icons/loyalty-points-medal';
import Link from 'components/link';
import Picture from 'components/picture';
import { ISanityImage as GWISanityImage } from 'generated/graphql-gateway';
import { IImageFragment } from 'generated/sanity-graphql';
import { primitive, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

import { modalToaster, rootToaster } from './refs';

const getToaster = () => modalToaster.current || rootToaster.current;

// applying defaults here because for some reason having multiple providers (as we need for modals) neither provider works for customization
// NOTE! there is also a patch-package file for this library for some other issues
const defaults: ToastOptions = {
  style: { backgroundColor: 'transparent', marginBottom: spacing.$2, padding: 0 },
  placement: 'top',
  duration: 3000, // per design
};

export const StandardToast: React.FC<{
  text: string;
  variant: 'info' | 'success' | 'error' | 'neutral';
}> = ({ text, variant }) => (
  <Alert
    maxWidth="100%"
    alignSelf="center"
    flexDirection="row"
    status={variant || 'neutral'}
    variant="black"
    accessibilityLiveRegion="polite"
    accessibilityHint=""
    marginX={0}
    marginBottom={0}
    alignItems="center"
  >
    {variant !== 'neutral' && <Alert.Icon />}
    <Text fontSize="md" fontWeight="medium" color="white" flexShrink={1} marginLeft={3}>
      {text}
    </Text>
  </Alert>
);

// this is because a modal could be dismissing and we need the toast on the root view not on the now gone modal
const TOAST_DELAY = 200;

export const addToCartToast = (
  message: string,
  image?: ISanityImage | IImageFragment | GWISanityImage | null,
  onRootToaster = true
) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={styles.addToCartWrap}>
        {image && (
          <View style={styles.pictureContainer}>
            <Picture alt={message.toString()} height="$12" width="$12" image={image} />
            <View style={styles.iconContainer}>
              <Icon variant="checkMarkGreen" size="$6" />
            </View>
          </View>
        )}
        <TextMessage>{message}</TextMessage>
      </View>,
      defaults
    );
  }, TOAST_DELAY);
};

const richToast = ({
  title,
  message,
  imageSource,
  options,
}: {
  title: string;
  message: string;
  imageSource?: any;
  options?: ToastOptions;
}) => {
  setTimeout(() => {
    (rootToaster.current || getToaster())?.show(
      <View style={styles.richToastWrap}>
        {imageSource && <Image alt={title} style={styles.richToastImage} source={imageSource} />}
        <View>
          <Text fontWeight="bold">{title}</Text>
          <Text>{message}</Text>
        </View>
      </View>,
      { ...defaults, ...options }
    );
  }, TOAST_DELAY);
};

const celebrationToast = ({
  title,
  message,
  points,
  onRootToaster = true,
}: {
  title: string;
  message: string;
  points: number;
  onRootToaster?: boolean;
}) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={celebrationToastStyles.wrapper}>
        <View style={celebrationToastStyles.iconContainer}>
          <LoyaltyPointsMedal points={points} size={60} />
        </View>
        <View style={celebrationToastStyles.contentContainer}>
          <Text style={celebrationToastStyles.title}>{title}</Text>
          <Text style={celebrationToastStyles.message}>{message}</Text>
        </View>
      </View>,
      {
        ...defaults,
        style: celebrationToastStyles.default,
      }
    );
  }, TOAST_DELAY);
};

// Waits until the store is fully changed
const VIEW_AVAILABLE_OFFERS_TOAST_DURATION = 5000;

const viewAvailableOffersToast = (message: string, linkText: string = '', onRootToaster = true) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={styles.viewAvailableOffersWrap}>
        <Link isInline to={routes.offers}>
          <View>
            <Text variant="copyOne" fontWeight="bold">
              {message}
            </Text>
          </View>
          <View>
            <Text textDecorationLine="underline">{linkText}</Text>
          </View>
        </Link>
      </View>,
      { ...defaults, duration: VIEW_AVAILABLE_OFFERS_TOAST_DURATION }
    );
  }, TOAST_DELAY);
};

const showToast = (message: string, variant: 'info' | 'success' | 'error' | 'neutral') => {
  setTimeout(() => {
    getToaster()?.show(<StandardToast text={message} variant={variant} />, defaults);
  }, TOAST_DELAY);
};

export const toaster = {
  hideAll: () => getToaster()?.hideAll(),
  addToCartToast,
  celebrationToast,
  viewAvailableOffersToast,
  richToast,
  informative: (message: string) => showToast(message, 'info'),
  positive: (message: string) => showToast(message, 'success'),
  negative: (message: string) => showToast(message, 'error'),
  neutral: (message: string) => showToast(message, 'neutral'),
};

const styles = StyleSheet.create({
  addToCartWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.$3,
    marginVertical: spacing.$1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: primitive.$blackOpacity4,
    elevation: 2,
  },
  richToastWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.$3,
    marginVertical: spacing.$1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    elevation: 2,
  },
  richToastImage: {
    height: 75,
    width: 75,
    marginRight: spacing.$3,
  },
  viewAvailableOffersWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    padding: spacing.$3,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: primitive.$bbqBrown,
    elevation: 2,
  },
  pictureContainer: {
    height: 60,
    width: 60,
    position: 'relative',
    borderWidth: 1,
    borderColor: primitive.$blackOpacity30,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    position: 'absolute',
    right: -4,
    top: -4,
    height: 15,
    width: 15,
  },
});

export const TextMessage = Text.withConfig({
  variant: 'copyOne',
  fontWeight: 'bold',
  flex: 1,
  marginLeft: spacing.$1,
});

const celebrationToastStyles = StyleSheet.create({
  default: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  wrapper: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    padding: 9,
    borderRadius: 10,
    borderWidth: 1,
    elevation: 2,
    borderColor: primitive.$bbqBrown,
    backgroundColor: primitive.$white,
  },
  title: {
    color: primitive.$bbqBrown,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: primitive.fontSizes.sm,
    fontWeight: 'normal',
  },
  message: {
    color: primitive.$bbqBrown,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 12,
    fontWeight: 'normal',
  },
  iconContainer: {
    marginHorizontal: 12,
  },
  contentContainer: {
    marginLeft: 12,
    display: 'flex',
    flexDirection: 'column',
  },
});
